import type { Block } from '@/types/content';

export default function ContentBlocks({ blocks }: { blocks: Block[] }) {
  return (
    <div className="prose-chigua">
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'h2':
            return (
              <h2
                key={i}
                id={`section-${i}`}
                className="scroll-mt-28 mt-10 mb-4 font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug"
              >
                {block.text}
              </h2>
            );
          case 'h3':
            return (
              <h3
                key={i}
                id={`section-${i}`}
                className="scroll-mt-28 mt-8 mb-3 font-heading text-lg font-semibold text-foreground-900"
              >
                {block.text}
              </h3>
            );
          case 'quote':
            return (
              <blockquote
                key={i}
                className="my-7 pl-5 border-l-2 border-primary-400 bg-primary-50/60 py-4 pr-4 rounded-r-md"
              >
                <p className="font-heading text-base md:text-lg leading-8 text-foreground-800">
                  {block.text}
                </p>
                {block.cite && (
                  <cite className="mt-2 block text-xs text-foreground-500 not-italic">
                    —— {block.cite}
                  </cite>
                )}
              </blockquote>
            );
          case 'list':
            return (
              <ul key={i} className="my-5 space-y-2.5">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-3 text-[16px] leading-8 text-foreground-700">
                    <span className="mt-3 shrink-0 w-1.5 h-1.5 rounded-full bg-accent-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            );
          case 'steps':
            return (
              <ol key={i} className="my-6 space-y-3.5">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-4">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold flex items-center justify-center">
                      {j + 1}
                    </span>
                    <span className="text-[16px] leading-8 text-foreground-700 pt-0.5">
                      {item}
                    </span>
                  </li>
                ))}
              </ol>
            );
          case 'image':
            return (
              <figure key={i} className="my-8">
                <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={block.src}
                    alt={block.alt}
                    width={1200}
                    height={800}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {block.caption && (
                  <figcaption className="mt-2.5 text-center text-xs text-foreground-500">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'table':
            return (
              <div key={i} className="my-7 overflow-x-auto rounded-lg border border-background-200">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-background-100">
                      {block.head.map((h) => (
                        <th
                          key={h}
                          className="text-left px-4 py-3 font-heading font-semibold text-foreground-900 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r} className="border-t border-background-200">
                        {row.map((cell, c) => (
                          <td
                            key={c}
                            className={`px-4 py-3 leading-7 ${
                              c === 0
                                ? 'font-medium text-foreground-900 whitespace-nowrap'
                                : 'text-foreground-600'
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return (
              <p key={i} className="text-[16px] md:text-[17px] leading-9 text-foreground-700">
                {block.text}
              </p>
            );
        }
      })}
    </div>
  );
}